// Montages: a handful of his photos laid out as one picture to keep or share.
// Every Sunday evening the server makes one of his week (and, every four
// weeks, a "growing up" one from the weekly portraits) and tells you it's
// ready; you can also make one from any photos you like. A montage is just
// the recipe — which photos, the title — so it syncs as a tiny record and
// each phone draws it at full quality from the full-size photos.

import { ageNow } from './age.ts';
import { DAY, MINUTE, formatDate } from './time.ts';
import { weekStart } from './trainingplan.ts';
import type { Millis, Montage, Photo, Profile } from './types.ts';

/** Most photos a montage holds: more and each one is too small to enjoy. */
export const MONTAGE_MAX = 9;
/** The finished picture: portrait 4:5, the shape Instagram and phone screens like. */
export const MONTAGE_SIZE = { w: 1080, h: 1350 } as const;

/**
 * Pick up to `max` photos spread across the time they cover: bursts (several
 * shots within a couple of minutes) count once, weekly portraits and
 * captioned photos win a tie, and the result is in time order.
 */
export function pickPhotos(photos: Photo[], max = MONTAGE_MAX): Photo[] {
  const sorted = [...photos].sort((a, b) => a.at - b.at);
  // One per burst: the best of any run of shots taken close together.
  const score = (p: Photo) => (p.weekly ? 2 : 0) + (p.caption ? 1 : 0) + (p.full ? 0.5 : 0);
  const bursts: Photo[] = [];
  for (const p of sorted) {
    const last = bursts[bursts.length - 1];
    if (last && p.at - last.at < 2 * MINUTE) {
      if (score(p) > score(last)) bursts[bursts.length - 1] = p;
    } else bursts.push(p);
  }
  if (bursts.length <= max) return bursts;
  // Evenly spaced through the period, so a busy Saturday doesn't crowd out the rest.
  const out: Photo[] = [];
  for (let i = 0; i < max; i++) out.push(bursts[Math.round((i * (bursts.length - 1)) / (max - 1))]);
  return out;
}

/** The latest weekly portrait for each week of age, oldest first. */
export function portraitsByWeek(photos: Photo[]): Photo[] {
  const byWeek = new Map<number, Photo>();
  for (const p of [...photos].sort((a, b) => a.at - b.at)) if (p.weekly && p.ageWeeks !== undefined) byWeek.set(p.ageWeeks, p);
  return [...byWeek.entries()].sort((a, b) => a[0] - b[0]).map(([, p]) => p);
}

/** Up to `max` portraits evenly spaced from the first to the latest — always both ends. */
export function growthPhotos(photos: Photo[], max = MONTAGE_MAX): Photo[] {
  const all = portraitsByWeek(photos);
  if (all.length <= max) return all;
  return Array.from({ length: max }, (_, i) => all[Math.round((i * (all.length - 1)) / (max - 1))]);
}

/**
 * The montages the server should make now, if any: his week on Sunday from
 * 6pm (when there are at least 3 photos), and a "growing up" montage when he
 * turns a multiple of four weeks (from 3 weekly portraits). Ids come from
 * the week, so it's only ever made once whoever — or whatever — asks.
 */
export function dueMontages(input: { photos: Photo[]; montages: Montage[]; profile?: Pick<Profile, 'dob' | 'name'>; now: Millis }): Montage[] {
  const { photos, montages, profile, now } = input;
  const d = new Date(now);
  if (d.getDay() !== 0 || d.getHours() < 18) return [];
  const name = profile?.name || 'Arlo';
  const monday = weekStart(now);
  const from = new Date(`${monday}T00:00`).getTime();
  const have = new Set(montages.map((m) => m.id));
  const age = profile ? ageNow(profile, now) : undefined;
  const out: Montage[] = [];

  const weekId = `auto-week-${monday}`;
  const week = pickPhotos(photos.filter((p) => p.at >= from && p.at <= now));
  if (!have.has(weekId) && week.length >= 3)
    out.push({
      id: weekId,
      at: now,
      kind: 'week',
      title: age ? `${name} at ${age.weeks} weeks` : `${name}’s week`,
      sub: `${formatDate(from)} – ${formatDate(now)}`,
      photos: week.map((p) => p.id),
      auto: true,
    });

  if (age && age.weeks >= 12 && age.weeks % 4 === 0) {
    const growId = `auto-growth-${age.weeks}`;
    const grow = growthPhotos(photos);
    if (!have.has(growId) && grow.length >= 3)
      out.push({
        id: growId,
        at: now,
        kind: 'growth',
        title: `${name}, growing up`,
        sub: `${grow[0].ageWeeks} to ${grow[grow.length - 1].ageWeeks} weeks`,
        photos: grow.map((p) => p.id),
        auto: true,
      });
  }
  return out;
}

/** A montage of any photos you choose (or a recent stretch). */
export function makeMontage(input: { photos: Photo[]; kind: Montage['kind']; title: string; sub?: string; now: Millis; id: string; by?: string }): Montage {
  const chosen = input.kind === 'growth' ? growthPhotos(input.photos) : input.kind === 'custom' ? input.photos.slice(0, MONTAGE_MAX) : pickPhotos(input.photos);
  return { id: input.id, at: input.now, kind: input.kind, title: input.title, sub: input.sub, photos: chosen.map((p) => p.id), by: input.by };
}

/** The photos from the last `days` days. */
export const recentPhotos = (photos: Photo[], days: number, now: Millis) => photos.filter((p) => p.at > now - days * DAY && p.at <= now);

export interface Cell {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** How many photos go on each row: rows of two or three read best on a tall picture. */
const ROWS: Record<number, number[]> = {
  1: [1],
  2: [1, 1],
  3: [1, 2],
  4: [2, 2],
  5: [2, 3],
  6: [2, 2, 2],
  7: [2, 3, 2],
  8: [3, 2, 3],
  9: [3, 3, 3],
};

/**
 * Where each photo goes, inside the area below the title. Rows of equal
 * height, photos of equal width within a row, `gap` between everything.
 */
export function montageLayout(n: number, area: Cell, gap: number): Cell[] {
  const rows = ROWS[Math.max(1, Math.min(MONTAGE_MAX, n))];
  const h = (area.h - gap * (rows.length - 1)) / rows.length;
  const cells: Cell[] = [];
  rows.forEach((count, r) => {
    const w = (area.w - gap * (count - 1)) / count;
    for (let c = 0; c < count; c++) cells.push({ x: area.x + c * (w + gap), y: area.y + r * (h + gap), w, h });
  });
  return cells.slice(0, n);
}

/** Crop a `sw`×`sh` image to fill a cell (like CSS object-fit: cover), favouring the upper middle — where a puppy's face usually is. */
export function coverCrop(sw: number, sh: number, cell: { w: number; h: number }): { sx: number; sy: number; sw: number; sh: number } {
  const scale = Math.max(cell.w / sw, cell.h / sh);
  const cw = cell.w / scale;
  const ch = cell.h / scale;
  return { sx: (sw - cw) / 2, sy: Math.max(0, (sh - ch) * 0.35), sw: cw, sh: ch };
}

/** "Arlo at 14 weeks montage is ready" → push text. */
export function montageAlertText(m: Montage): { title: string; body: string } {
  return m.kind === 'growth'
    ? { title: 'New montage: growing up 🐾', body: `${m.title} — ${m.sub ?? ''}. Tap to see how much he’s changed.` }
    : { title: 'His week in pictures 📸', body: `${m.title}: ${m.photos.length} photos from this week, ready to keep or share.` };
}

export interface Memory {
  photo: Photo;
  /** "A week ago today", "A month ago today". */
  label: string;
}

/**
 * A photo to bring back on the Today screen: from a month ago (give or take
 * three days), else a week ago (give or take a day) — the one nearest the
 * date, a weekly portrait if there's a choice. Undefined when there's none.
 */
export function albumMemory(photos: Photo[], now: Millis): Memory | undefined {
  const near = (days: number, slack: number) => {
    const target = now - days * DAY;
    const close = photos.filter((p) => Math.abs(p.at - target) <= slack * DAY);
    return close.sort((a, b) => Number(!!b.weekly) - Number(!!a.weekly) || Math.abs(a.at - target) - Math.abs(b.at - target))[0];
  };
  const month = near(30, 3);
  if (month) return { photo: month, label: 'A month ago' };
  const week = near(7, 1);
  if (week) return { photo: week, label: 'A week ago' };
  return undefined;
}
